import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiError, resolveImage } from '@/lib/api';
import { Field, Card } from '@/admin/fields';

function ProductItem({ product, index, onChange, onRemove }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd);
      onChange('image', data.path);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card title={product.name || 'New product'}>
      <div className="flex gap-4 items-start">
        <div className="w-24 shrink-0">
          <div className="aspect-[3/4] bg-yunti-sand border border-yunti-ink/10 overflow-hidden flex items-center justify-center">
            {product.image ? (
              <img src={resolveImage(product.image)} alt={product.alt || product.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-[9px] font-mono text-yunti-ink/40 text-center px-1">NO IMAGE</span>
            )}
          </div>
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="px-3 py-2 bg-yunti-ink text-white text-[11px] font-mono uppercase tracking-widest hover:bg-yunti-purple transition-colors disabled:opacity-60"
              data-testid={`upload-product-image-${index}`}
            >
              {uploading ? 'Uploading...' : 'Upload photo'}
            </button>
            {product.image && (
              <button
                type="button"
                onClick={() => onChange('image', '')}
                className="px-3 py-2 border border-yunti-ink/30 text-[11px] font-mono uppercase tracking-widest hover:border-red-600 hover:text-red-700 transition-colors"
                data-testid={`remove-product-image-${index}`}
              >
                Remove photo
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadImage(e.target.files?.[0])}
            data-testid={`file-input-${index}`}
          />
          <Field label="Or paste image URL" value={product.image} onChange={(v) => onChange('image', v)} testId={`edit-product-image-${index}`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Product name" value={product.name} onChange={(v) => onChange('name', v)} testId={`edit-product-name-${index}`} />
        <Field label="Badge" value={product.badge} onChange={(v) => onChange('badge', v)} testId={`edit-product-badge-${index}`} />
      </div>
      <Field label="Image alt text" value={product.alt} onChange={(v) => onChange('alt', v)} testId={`edit-product-alt-${index}`} />
      <button
        type="button"
        onClick={onRemove}
        className="text-xs font-mono uppercase text-red-700 hover:underline"
        data-testid={`remove-product-${index}`}
      >
        Remove this product
      </button>
    </Card>
  );
}

export function ProductsTab({ content, update }) {
  const items = content.products.items;
  const setItem = (i, key, v) => update('products.items', items.map((p, idx) => (idx === i ? { ...p, [key]: v } : p)));

  const addItem = () =>
    update('products.items', [
      ...items,
      { id: `product-${Date.now()}`, name: 'New Product', badge: 'Drop 001 • Preview', image: '', alt: '' },
    ]);

  return (
    <div className="space-y-6">
      <Card title="Section header & storefront notice">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={content.products.eyebrow} onChange={(v) => update('products.eyebrow', v)} testId="edit-products-eyebrow" />
          <Field label="Title" value={content.products.title} onChange={(v) => update('products.title', v)} testId="edit-products-title" />
        </div>
        <Field label="Side note" value={content.products.note} onChange={(v) => update('products.note', v)} testId="edit-products-note" />
        <Field label="Notice" value={content.products.notice} onChange={(v) => update('products.notice', v)} testId="edit-products-notice" />
        <Field label="Notice accent" value={content.products.noticeAccent} onChange={(v) => update('products.noticeAccent', v)} testId="edit-products-notice-accent" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Preview label" value={content.products.previewLabel} onChange={(v) => update('products.previewLabel', v)} testId="edit-products-preview-label" />
          <Field label="Preview sub-line" value={content.products.previewSub} onChange={(v) => update('products.previewSub', v)} testId="edit-products-preview-sub" />
        </div>
      </Card>

      {items.map((product, i) => (
        <ProductItem
          key={product.id}
          product={product}
          index={i}
          onChange={(key, v) => setItem(i, key, v)}
          onRemove={() => update('products.items', items.filter((_, idx) => idx !== i))}
        />
      ))}

      <button
        type="button"
        onClick={addItem}
        className="px-4 py-2 border border-yunti-ink text-xs font-mono uppercase tracking-widest hover:bg-yunti-ink hover:text-white transition-colors"
        data-testid="add-product"
      >
        + Add product
      </button>
    </div>
  );
}
